import analytics from "$/util/analytics";
import { getCreationSlug } from "@blackink/core/creation/util";
import { Download } from "@mui/icons-material";
import ImageIcon from "@mui/icons-material/Image";
import PhotoSizeSelectLargeIcon from "@mui/icons-material/PhotoSizeSelectLarge";
import PrintIcon from "@mui/icons-material/Print";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useAuth } from "../util/auth";
import { getCreationBucket } from "../util/creation_util";
import {
  createEvent,
  createJob,
  getImageUrl,
  refreshUser,
  subscribeJob,
} from "../util/db";
import supabase from "../util/supabase";
import PrintImage from "./PrintImage";
import ProBadge from "./icons/ProBadge";

function DownloadButton(props) {
  const { creation, idx, buttonProps, variant = "button" } = props;
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [alert, setAlert] = useState(null);

  const [printItem, setPrintItem] = useState(null);
  const printRef = useRef();

  const theme = useTheme();
  const auth = useAuth();
  const navigate = useNavigate();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  useEffect(() => {
    if (printItem) {
      handlePrint();
      setPrintItem(null);
    }
  }, [printItem]);

  const downloadImage = async (imageUrl, downloadName, bucket = "creations") => {
    try {
      // Fetch the image as a blob
      const response = await fetch(getImageUrl(imageUrl, bucket));
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const blobUrl = URL.createObjectURL(blob);

      // Create a link element
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadName; // Set the download attribute with the desired file name
      document.body.appendChild(link);
      link.click(); // Programmatically click the link to trigger the download

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl); // Revoke the object URL to free memory

      console.log(`Image downloaded successfully as ${downloadName}`);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setPending(false);
    setAlert(null);
    supabase.removeAllChannels();
  };

  const handleError = () => {
    setPending(false);
    setAlert("Download failed. Please try again.");
    supabase.removeAllChannels();
  };

  const handleJobEvent = (jobEvent) => {
    if (jobEvent.new.status === "SUCCESS") {
      auth.user && refreshUser(auth.user.id);
      downloadImage(
        jobEvent.new.result.image_url,
        `${getCreationSlug(creation, false)}_4x.png`,
        "jobs",
      );
      handleClose();
    } else if (jobEvent.new.status === "ERROR") {
      handleError();
    }
  };
  const dlTypes = {
    highres: {
      id: "highres",
      icon: <PhotoSizeSelectLargeIcon />,
      title: "4X Super Res Image",
      visible: true,
      highlight: (
        <>
          <ProBadge
            color={theme.palette.primary.contrastText}
            height={18}
            sx={{ ml: -0.5, mb: -0.5 }}
          />
          Pro Only
        </>
      ),
      handle: async () => {
        const customerExpired =
          !auth.user || (auth.user.credits < 6 && !auth.user.isActiveSubscription);
        if (customerExpired) {
          if (!auth.user) {
            navigate("/login");
          } else if (auth.user.stripeSubscriptionId) {
            navigate("/settings/billing");
          } else {
            navigate("/pricing");
          }
          return;
        }
        setPending(true);
        try {
          const response = await createJob(
            auth.user && auth.user.id,
            {
              idx,
              creation_id: creation.id,
              image_url: getImageUrl(
                creation.imagePaths[idx],
                getCreationBucket(creation),
              ),
            },
            "upscale",
          );
          const upscaleJob = response[0];

          subscribeJob(upscaleJob.id, handleJobEvent);
        } catch (err) {
          handleError();
        }
      },
    },
    webp: {
      id: "webp",
      icon: <ImageIcon />,
      title: "Low Res Image",
      visible: true,
      highlight: false,
      handle: () => {
        console.log("Downloading webp image:", idx);
        downloadImage(
          creation.imagePaths[idx],
          `${getCreationSlug(creation, false)}.webp`,
          getCreationBucket(creation),
        );
        handleClose();
      },
    },
    // svg: {
    //   id: "svg",
    //   icon: <PolylineIcon />,
    //   title: "Vector (svg)",
    //   visible: creation.style === "minimalist",
    //   highlight: false,
    //   handle: () => {
    //     const svgPath = `${creation.imagePaths[idx]
    //       .split(".")
    //       .slice(0, -1)
    //       .join(".")}.svg`;
    //     downloadImage(svgPath, `${getCreationSlug(creation, false)}.svg`);
    //     handleClose();
    //   },
    // },
    pdf: {
      id: "pdf",
      icon: <PrintIcon />,
      title: "Printout (pdf)",
      visible: true,
      highlight: false,
      handle: () => {
        setPrintItem(creation.imagePaths[idx]);
        handleClose();
      },
    },
  };

  const handleListItemClick = (type) => {
    analytics.track("download", { type, creationId: creation.id, idx });
    createEvent("download", creation.id, idx);

    dlTypes[type].handle();
  };

  const PendingView = (
    <div style={{ textAlign: "center", padding: 20 }}>
      <CircularProgress />
      <Typography paddingTop={2}>
        Generating 4x Super Res Image. <br />
        Takes about 10 seconds.
      </Typography>
    </div>
  );

  const menuItems = Object.values(dlTypes).filter((type) => type.visible);

  const MenuView = (
    <List sx={{ pt: 0 }}>
      {menuItems.map((type, typeIdx) => (
        <React.Fragment key={type.id}>
          <ListItem>
            <ListItemButton
              onClick={() => handleListItemClick(type.id)}
              sx={{ color: type.highlight && theme.palette.primary.main }}
            >
              <ListItemIcon
                sx={{ color: type.highlight && theme.palette.primary.main }}
              >
                {type.icon}
              </ListItemIcon>
              <ListItemText
                primary={type.title}
                secondary={
                  type.highlight && (
                    <Chip label={type.highlight} color="secondary" size="small" />
                  )
                }
              />
            </ListItemButton>
          </ListItem>
          {typeIdx !== menuItems.length - 1 && (
            <Divider variant="middle" component="li" />
          )}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <>
      {variant === "button" ? (
        <Button
          variant="outlined"
          onClick={() => setOpen(true)}
          startIcon={<Download />}
          fullWidth
          sx={{ px: 2 }}
          {...buttonProps}
        >
          Download
        </Button>
      ) : (
        <IconButton
          aria-label="download"
          onClick={() => setOpen(true)}
          sx={{ borderRadius: "25%", minWidth: 40, height: 40 }}
        >
          <Download />
        </IconButton>
      )}
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle>{pending ? "Downloading..." : "Download Design"}</DialogTitle>
        {alert && <Alert severity="error">{alert}</Alert>}
        {pending ? PendingView : MenuView}
      </Dialog>
      <PrintImage
        printRef={printRef}
        imgPath={printItem}
        bucket={getCreationBucket(creation)}
      />
    </>
  );
}

export default DownloadButton;
